import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ApiService from "services/ApiService";
import { signOut } from "./authSlice";
import moment from "moment";
import _ from "lodash";
import { OrderStatus } from "constants/OrderStatusConstant";
import { VendorOrderStatus } from "constants/VendorOrderStatusConstant";

export const initialState = {
  orders: [],
  integrationOrders: [],
  vendorOrders: [],
  kitchenOrders: [],
  kitchenOrdersList: [],
  actionRequiredOrders: [],
  rejectedOrders: [],
  loading: false,
  message: "",
  showMessage: false,
  success: true,
  totalPages: 1,
  totalRecords: 0,
  pageSize: 50,
  airports: [],
  fbos: [],
  orderStatus: [],
  caterers: [],
  search: "",
  searchField: "default",
  status: [],
  airport: [],
  fbo: [],
  caterer: "All Caterer's",
  currentPage: 1,
  sortField: "",
  sortOrder: "",
  filter: "",
  startDate: moment().add(-7, "days"),
  endDate: moment().add(7, "days"),
  kitchenContacts: [],
  userViews: [],
  vendorViews: [],
  vendorStartDate: moment().format("YYYY-MM-DD HH:mm"),
  vendorEndDate: moment().add(36, "hours").format("YYYY-MM-DD HH:mm"),
  kitchenStartDate: moment().format("YYYY-MM-DD HH:mm"),
  kitchenEndDate: moment().add(36, "hours").format("YYYY-MM-DD HH:mm"),
  actionRequiredStartDate: moment().format("YYYY-MM-DD"),
  actionRequiredEndDate: moment().add(30, "days").format("YYYY-MM-DD"),
  rejectedOrderStartDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
  rejectedOrderEndDate: moment().add(30, "days").format("YYYY-MM-DD"),
  cateringApiToken: {
    token: null,
    expiresAt: 0,
  },
  kitchens: [],
  isKitchenRadioGroupSelected: true,
  isDefaultKitchenRadioGroupSelected: true,
  isVendorRadioGroupSelected: true,
  isDefaultVendorRadioGroupSelected: true,
  modeOfDelivery: [],
  kitchen: "All Kitchens",
  compactUI: true,
};

export const fetchOrders = createAsyncThunk(
  "order/fetchOrders",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getOrders(data);
      if (response.status === 200) {
        return {
          orders: response.data.value,
          airports: response.data["@search.facets"]["airport/icao"].map(
            ({ count, value }) => ({
              key: value,
              value: `${value} (${count})`,
            }),
          ),
          fbos: response.data["@search.facets"]["fbo/name"].map(
            ({ value }) => value,
          ),
          orderStatus: response.data["@search.facets"].orderStatus.map(
            (item) => item.value,
          ),
          caterers: response.data["@search.facets"][
            "supplier/friendlyName"
          ].map((item) => item.value),
          totalRecords: response.data["@odata.count"],
          totalPages: Math.floor(response.data["@odata.count"] / 50),
        };
      } else if (response.status === 401) {
        return rejectWithValue(response.data);
      } else return initialState;
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching orders.",
      );
    }
  },
);

export const fetchOrderById = createAsyncThunk(
  "order/fetchOrderById",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getOrderById(data);
      if (response.status === 200) {
        return response.data.value;
      } else if (response.status === 401) {
        return rejectWithValue(response.data);
      } else return initialState;
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching order details.",
      );
    }
  },
);

export const fetchOrderRevisionHistory = createAsyncThunk(
  "order/fetchOrderRevisionHistory",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getOrderRevision(data);
      if (response.status === 200) {
        let { success, message, data } = response.data;
        return {
          success: success,
          message: message,
          data: data.length > 0 ? response.data.data[0].revisions : [],
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while fetching order revision history.",
      );
    }
  },
);

export const fetchSalesOrderVendorRevisionHistory = createAsyncThunk(
  "order/fetchSalesOrderVendorRevisionHistory",
  async (data, { rejectWithValue }) => {
    try {
      const response =
        await ApiService.getSalesOrderVendorRevisionHistory(data);
      if (response?.status === 200) {
        const responseBody = response?.data ?? {};
        const vendorOrders = Array.isArray(responseBody?.data)
          ? responseBody.data
          : [];
        const history = Array.isArray(vendorOrders[0]?.history)
          ? vendorOrders[0].history
          : [];

        return {
          success: responseBody?.success ?? true,
          message: responseBody?.message ?? "",
          data: history,
        };
      }

      return rejectWithValue(
        response?.data ??
          "Something went wrong while fetching sales order vendor revision history.",
      );
    } catch (err) {
      return rejectWithValue(
        err?.response?.data ||
          err?.response ||
          "Something went wrong while fetching sales order vendor revision history.",
      );
    }
  },
);

export const fetchOrderNotes = createAsyncThunk(
  "order/fetchOrderNotes",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getOrderRevision(data);
      if (response.status === 200) {
        let { success, message, data } = response.data;
        return {
          success: success,
          message: message,
          data: data,
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching order notes.",
      );
    }
  },
);

export const addOrderNotes = createAsyncThunk(
  "order/addOrderNotes",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.addSalesOrderNotes(data);
      if (response.status === 200) {
        let { success, message, data } = response.data;
        return {
          success: success,
          message: message,
          data: data,
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching order notes.",
      );
    }
  },
);

export const fetchIntegrationOrders = createAsyncThunk(
  "order/fetchIntegrationOrders",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getIntegrationOrders(data);
      if (response.status === 200) {
        return {
          integrationOrders: response.data.value,
          totalRecords: response.data["@odata.count"],
          totalPages: Math.floor(response.data["@odata.count"] / 50),
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while fetching integration orders.",
      );
    }
  },
);

export const fetchKitchenOrders = createAsyncThunk(
  "order/fetchKitchenOrders",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getKitchenOrders(data);
      if (response.status === 200) {
        return {
          orders: response.data.value,
          totalRecords: response.data["@odata.count"],
          totalPages: Math.floor(response.data["@odata.count"] / 50),
          kitchensList: response.data["@search.facets"][
            "supplier/friendlyName"
          ].map(({ value }) => value),
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching orders.",
      );
    }
  },
);

export const fetchVendorOrders = createAsyncThunk(
  "order/fetchVendorOrders",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getVendorOrders(data);
      if (response.status === 200) {
        const rejectedOrdersCount = response.data.value.reduce(
          (count, order) =>
            count + (order.vendorStatus === VendorOrderStatus.Rejected ? 1 : 0),
          0,
        );
        return {
          orders: response.data.value,
          airportList: response.data["@search.facets"]["airport/icao"].map(
            ({ value }) => value,
          ),
          fboList: response.data["@search.facets"]["fbo/name"].map(
            ({ value }) => value,
          ),
          totalRecords: response.data["@odata.count"] - rejectedOrdersCount,
        };
      } else if (response.status === 401) {
        return rejectWithValue(response.data);
      } else return initialState;
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching orders.",
      );
    }
  },
);

const getNonEmptyFacetValues = (facets, facetName) =>
  (facets?.[facetName] ?? [])
    .map((item) =>
      typeof item?.value === "string" ? item.value.trim() : item?.value,
    )
    .filter((value) => value !== null && value !== undefined && value !== "");

export const fetchVendorDashboardOrders = createAsyncThunk(
  "order/fetchVendorDashboardOrders",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getVendorDashboardOrders(data);
      if (response.status === 200) {
        const facets = response.data["@search.facets"];

        return {
          orders: response.data.value,
          airportList: getNonEmptyFacetValues(facets, "airport/icao"),
          fboList: getNonEmptyFacetValues(facets, "fbo/name"),
          supplierList: getNonEmptyFacetValues(facets, "supplier/friendlyName"),
          customerList: getNonEmptyFacetValues(facets, "salesName"),
          orderStatusList: getNonEmptyFacetValues(facets, "orderStatus"),
          totalRecords: response.data["@odata.count"],
        };
      } else if (response.status === 401) {
        return rejectWithValue(response.data);
      } else return initialState;
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching orders.",
      );
    }
  },
);

export const validateVendorOrderStatus = createAsyncThunk(
  "order/validateVendorOrderStatus",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.validateVendorOrderStatus(data);
      if (response?.status === 200) {
        return response.data;
      }

      return rejectWithValue(response?.data);
    } catch (err) {
      return rejectWithValue(
        err.response?.data ||
          "Something went wrong while validating the order link.",
      );
    }
  },
);

const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

export const fetchDashboardKitchenOrders = createAsyncThunk(
  "order/fetchDashboardKitchenOrders",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getDashboardKitchenOrders(data);
      if (response.status === 200) {
        return {
          orders: response.data.value,
          currencies: response.data["@search.facets"]["currency"].map(
            ({ value }) => value,
          ),
          airportList: response.data["@search.facets"]["airport/icao"].map(
            ({ value }) => value,
          ),
          fboList: response.data["@search.facets"]["fbo/name"].map(
            ({ value }) => value,
          ),
          kitchensList: response.data["@search.facets"][
            "supplier/friendlyName"
          ].map(({ value }) => value),
          deliveryModes: response.data["@search.facets"]["modeofDelivery"].map(
            ({ value }) => ({
              key: value,
              value:
                value === "99"
                  ? `Retail Orders`
                  : `${capitalizeFirst(_.toLower(value))} Orders`,
            }),
          ),
          totalRecords: response.data["@odata.count"],
        };
      } else if (response.status === 401) {
        return rejectWithValue(response.data);
      } else return initialState;
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching orders.",
      );
    }
  },
);

export const fetchPowerBiReport = createAsyncThunk(
  "order/fetchPowerBiReport",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getPowerBIReport(data);
      if (response?.status === 200) {
        return response.data;
      }
      return rejectWithValue(response?.data || "Unable to load Power BI report.");
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching Power BI report.",
      );
    }
  },
);

export const fetchKitchenContacts = createAsyncThunk(
  "order/fetchKitchenContacts",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getKitchenContacts();
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching kitchen contacts.",
      );
    }
  },
);

export const fetchKitchens = createAsyncThunk(
  "order/fetchKitchens",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getKitchens();
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching kitchens list.",
      );
    }
  },
);

export const fetchIntegrationOrderUserView = createAsyncThunk(
  "order/fetchIntegrationOrderUserView",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getIntegrationOrderUserView(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching user views.",
      );
    }
  },
);

export const saveIntegrationOrderUserView = createAsyncThunk(
  "order/saveIntegrationOrderUserView",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.saveIntegrationOrderUserView(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while saving user view.",
      );
    }
  },
);

export const saveVendorOrderDashboardUserView = createAsyncThunk(
  "order/saveVendorOrderDashboardUserView",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.saveIntegrationOrderUserView(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while saving user view.",
      );
    }
  },
);

export const fetchVendorOrdersUserView = createAsyncThunk(
  "order/fetchVendorOrdersUserView",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getVendorOrderUserView(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching user views.",
      );
    }
  },
);

export const saveVendorOrderStatus = createAsyncThunk(
  "order/saveVendorOrderStatus",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.recordVendorStatus(data);
      if (response.status === 200) {
        return {
          success: response.data.success,
          message: response.data.message,
          data,
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while saving order status.",
      );
    }
  },
);

export const saveVendorOrderStatusFromEmail = createAsyncThunk(
  "order/saveVendorOrderStatusFromEmail",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.recordVendorActionFromEmail(data);
      if (response.status === 200) {
        return {
          success: response.data.success,
          message: response.data.message,
          data,
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while saving order status.",
      );
    }
  },
);

export const saveMultipleVendorOrderStatus = createAsyncThunk(
  "order/saveMultipleVendorOrderStatus",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.recordVendorStatus(data);
      if (response.status === 200) {
        return {
          success: response.data.success,
          message: response.data.message,
          data,
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while saving order status.",
      );
    }
  },
);

export const saveKitchenOrderStatus = createAsyncThunk(
  "order/saveKitchenOrderStatus",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.recordKitchenStatus(data);
      if (response.status === 200) {
        return {
          success: response.data.success,
          message: response.data.message,
          data,
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue({
        success: false,
        message: err.response || "Error",
      });
    }
  },
);

export const saveMultipleKitchenOrderStatus = createAsyncThunk(
  "order/saveMultipleKitchenOrderStatus",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.recordKitchenStatus(data);
      if (response.status === 200) {
        return {
          success: response.data.success,
          message: response.data.message,
          data,
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue({
        success: false,
        message: err.response || "Error",
      });
    }
  },
);

export const markOrderDelivered = createAsyncThunk(
  "order/markOrderDelivered",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.markOrderDelivered(data);
      if (response.status === 200) {
        return {
          success: response.data.success,
          message: response.data.message,
          data,
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while marking order as delivered.",
      );
    }
  },
);

export const markMultipleOrderDelivered = createAsyncThunk(
  "order/markMultipleOrderDelivered",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.markOrderDelivered(data);
      if (response.status === 200) {
        return {
          success: response.data.success,
          message: response.data.message,
          data,
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while marking order as delivered.",
      );
    }
  },
);

export const generateDeeplink = createAsyncThunk(
  "order/generateDeeplink",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.generateDeeplink(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while generating deeplink for an order.",
      );
    }
  },
);

export const cancellationOrderRequest = createAsyncThunk(
  "order/cancellationOrderRequest",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.cancellationOrderRequest(data);
      if (response.status === 200) {
        return {
          success: response.data.success,
          message: response.data.message,
          data,
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while requesting order cancellation.",
      );
    }
  },
);

export const getCateringApiToken = createAsyncThunk(
  "order/getCateringApiToken",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getCateringApiToken();
      if (response.status === 200) {
        return response.data.data[0];
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong. Please try again later.",
      );
    }
  },
);

export const getFbosByAirport = createAsyncThunk(
  "order/getFbosByAirport",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getFbosByAirport(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching FBOs.",
      );
    }
  },
);

export const updateOrderHeader = createAsyncThunk(
  "order/updateOrderHeader",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.updateOrderHeader(data);
      if (response.status === 200 || response.status === 202) {
        return {
          data,
          success: response.data.success,
          message: response.data.message,
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while updating order details.",
      );
    }
  },
);

export const updateKitchenProcessingStatus = createAsyncThunk(
  "order/updateKitchenProcessingStatus",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.updateKitchenProcessingStatus(data);
      if (response.status === 200 || response.status === 202) {
        return {
          data,
          success: response.data.success,
          message: response.data.message,
        };
      } else {
        const { message } = response.data;
        return rejectWithValue(message);
      }
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while updating order details.",
      );
    }
  },
);

export const updateMutpleOrdersKitchenProcessingStatus = createAsyncThunk(
  "order/updateMutpleOrdersKitchenProcessingStatus",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.updateKitchenProcessingStatus(data);
      if (response.status === 200 || response.status === 202) {
        return {
          data,
          success: response.data.success,
          message: response.data.message,
        };
      } else {
        const { message } = response.data;
        return rejectWithValue(message);
      }
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while updating order details.",
      );
    }
  },
);

export const fetchIntegratorByAlias = createAsyncThunk(
  "order/fetchIntegratorByAlias",
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await ApiService.getIntegratorByAlias(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while fetching integrator details.",
      );
    }
  },
);

export const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    setSearchText: (state, action) => {
      state.search = action.payload;
    },
    setSearchField: (state, action) => {
      state.searchField = action.payload;
    },
    setSelectedStatus: (state, action) => {
      state.status = action.payload;
    },
    setSelectedAirport: (state, action) => {
      state.airport = action.payload;
    },
    setSelectedKitchenFilter: (state, action) => {
      state.kitchen = action.payload;
    },
    setSelectedFbo: (state, action) => {
      state.fbo = action.payload;
    },
    setSelectedCaterer: (state, action) => {
      state.caterer = action.payload;
    },
    setSortField: (state, action) => {
      state.sortField = action.payload;
    },
    setSelectedPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    setFilter: (state, action) => {
      state.filter = action.payload;
    },
    setDate: (state, action) => {
      state.startDate = action.payload.startDate;
      state.endDate = action.payload.endDate;
    },
    clearMessage: (state, action) => {
      state.message = null;
    },
    setVendorDate: (state, action) => {
      state.vendorStartDate = action.payload.startDate;
      state.vendorEndDate = action.payload.endDate;
      state.isVendorRadioGroupSelected =
        action.payload.isVendorRadioGroupSelected;
      state.isDefaultVendorRadioGroupSelected =
        action.payload.isDefaultVendorRadioGroupSelected;
    },
    setKitchenDate: (state, action) => {
      state.kitchenStartDate = action.payload.startDate;
      state.kitchenEndDate = action.payload.endDate;
      state.isKitchenRadioGroupSelected =
        action.payload.isKitchenRadioGroupSelected;
      state.isDefaultKitchenRadioGroupSelected =
        action.payload.isDefaultKitchenRadioGroupSelected;
    },
    setActionRequiredDate: (state, action) => {
      state.actionRequiredStartDate = action.payload.startDate;
      state.actionRequiredEndDate = action.payload.endDate;
    },
    setRejectedOrderDate: (state, action) => {
      state.rejectedOrderStartDate = action.payload.startDate;
      state.rejectedOrderEndDate = action.payload.endDate;
    },
    resetFilter: (state, action) => {
      state.caterer = "All Caterer's";
    },
    setCompactUI: (state, action) => {
      state.compactUI = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        const {
          orders,
          airports,
          fbos,
          orderStatus,
          caterers,
          totalPages,
          totalRecords,
        } = action.payload;
        return {
          ...state,
          loading: false,
          orders,
          airports,
          fbos,
          orderStatus,
          totalPages,
          totalRecords,
          caterers,
        };
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(fetchOrderById.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(fetchOrderRevisionHistory.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchOrderRevisionHistory.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(fetchOrderRevisionHistory.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(fetchSalesOrderVendorRevisionHistory.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(
        fetchSalesOrderVendorRevisionHistory.fulfilled,
        (state, action) => {
          return {
            ...state,
            loading: false,
          };
        },
      )
      .addCase(
        fetchSalesOrderVendorRevisionHistory.rejected,
        (state, action) => {
          return {
            ...state,
            success: false,
            loading: false,
            message: action.payload,
            showMessage: true,
          };
        },
      )
      .addCase(fetchOrderNotes.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchOrderNotes.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(fetchOrderNotes.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(addOrderNotes.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(addOrderNotes.fulfilled, (state, action) => {
        const { data, success, message } = action.payload;
        return {
          ...state,
          success: success,
          message: message,
          loading: false,
        };
      })
      .addCase(addOrderNotes.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(fetchVendorOrders.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchVendorOrders.fulfilled, (state, action) => {
        const { orders, airportList, fboList } = action.payload;
        return {
          ...state,
          loading: false,
          vendorOrders: orders,
          airports: airportList,
          fbos: fboList,
        };
      })
      .addCase(fetchVendorOrders.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(fetchVendorDashboardOrders.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchVendorDashboardOrders.fulfilled, (state, action) => {
        const { orders } = action.payload;
        return {
          ...state,
          loading: false,
          vendorOrders: orders,
        };
      })
      .addCase(fetchVendorDashboardOrders.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(validateVendorOrderStatus.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(validateVendorOrderStatus.fulfilled, (state) => {
        return { ...state, loading: false };
      })
      .addCase(validateVendorOrderStatus.rejected, (state) => {
        return { ...state, loading: false };
      })
      .addCase(fetchDashboardKitchenOrders.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchDashboardKitchenOrders.fulfilled, (state, action) => {
        const { orders, airportList, fboList, kitchensList, deliveryModes } =
          action.payload;
        return {
          ...state,
          loading: false,
          kitchenOrders: orders,
          airports: airportList,
          fbos: fboList,
          kitchens: kitchensList,
          modeOfDelivery: deliveryModes,
        };
      })
      .addCase(fetchDashboardKitchenOrders.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(fetchPowerBiReport.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchPowerBiReport.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(fetchPowerBiReport.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: false,
        };
      })
      .addCase(fetchKitchenOrders.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchKitchenOrders.fulfilled, (state, action) => {
        // const { orders } = action.payload;
        return {
          ...state,
          loading: false,
          // kitchenOrdersList: orders,
        };
      })
      .addCase(fetchKitchenOrders.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(fetchKitchenContacts.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchKitchenContacts.fulfilled, (state, action) => {
        const { data } = action.payload;
        return {
          ...state,
          loading: false,
          kitchenContacts: data,
        };
      })
      .addCase(fetchKitchenContacts.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(fetchKitchens.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchKitchens.fulfilled, (state, action) => {
        const { data } = action.payload;
        return {
          ...state,
          loading: false,
          kitchens: data,
        };
      })
      .addCase(fetchKitchens.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(fetchIntegrationOrderUserView.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchIntegrationOrderUserView.fulfilled, (state, action) => {
        const { data } = action.payload;
        return {
          ...state,
          loading: false,
          userViews: data,
        };
      })
      .addCase(fetchIntegrationOrderUserView.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(fetchVendorOrdersUserView.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchVendorOrdersUserView.fulfilled, (state, action) => {
        const { data } = action.payload;
        return {
          ...state,
          loading: false,
          vendorViews: data,
        };
      })
      .addCase(fetchVendorOrdersUserView.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
          showMessage: true,
        };
      })
      .addCase(saveIntegrationOrderUserView.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(saveIntegrationOrderUserView.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        return {
          ...state,
          loading: false,
          userViews: [
            {
              id: data.id,
              viewId: data.views[0].id,
              name: data.views[0].name,
              type: data.views[0].type,
              addedOn: data.views[0].addedOn,
              columns: data.views[0].columns,
              columnOrdering: data.views[0].columnOrdering,
            },
          ],
          message: message,
          success: success,
        };
      })
      .addCase(saveIntegrationOrderUserView.rejected, (state, action) => {
        return {
          ...state,
          success: false,
          loading: false,
          message: action.payload,
        };
      })
      .addCase(saveVendorOrderStatus.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(saveVendorOrderStatus.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        const { salesOrderId, status } = data;
        const editedOrders = state.vendorOrders.map((a) => {
          if (a.salesOrderId === salesOrderId)
            return {
              ...a,
              vendorStatus: status,
            };
          else return a;
        });
        return {
          ...state,
          vendorOrders: editedOrders,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(saveVendorOrderStatus.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(saveVendorOrderStatusFromEmail.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(saveVendorOrderStatusFromEmail.fulfilled, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(saveVendorOrderStatusFromEmail.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(saveKitchenOrderStatus.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(saveKitchenOrderStatus.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        const { salesOrderId, status, orderStatus } = data;
        const editedOrders = state.kitchenOrders.map((a) => {
          if (a.salesOrderId === salesOrderId)
            return {
              ...a,
              vendorStatus: status,
              orderStatus: OrderStatus.CONFIRMED,
            };
          else return a;
        });
        return {
          ...state,
          kitchenOrders: editedOrders,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(saveKitchenOrderStatus.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(saveMultipleKitchenOrderStatus.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(saveMultipleKitchenOrderStatus.fulfilled, (state, action) => {
        const { data } = action.payload;
        const { salesOrderId, status, orderStatus } = data;
        const editedOrders = state.kitchenOrders.map((a) => {
          if (a.salesOrderId === salesOrderId)
            return {
              ...a,
              vendorStatus: status,
              orderStatus: OrderStatus.CONFIRMED,
            };
          else return a;
        });
        return {
          ...state,
          kitchenOrders: editedOrders,
          loading: false,
        };
      })
      .addCase(saveMultipleKitchenOrderStatus.rejected, (state, action) => {
        // const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          // message: message,
          // success: success,
        };
      })
      .addCase(saveMultipleVendorOrderStatus.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(saveMultipleVendorOrderStatus.fulfilled, (state, action) => {
        const { data } = action.payload;
        const { salesOrderId, status, orderStatus } = data;
        const editedOrders = state.vendorOrders.map((a) => {
          if (a.salesOrderId === salesOrderId)
            return {
              ...a,
              vendorStatus: status,
            };
          else return a;
        });
        return {
          ...state,
          vendorOrders: editedOrders,
          loading: false,
        };
      })
      .addCase(saveMultipleVendorOrderStatus.rejected, (state, action) => {
        // const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          // message: message,
          // success: success,
        };
      })
      .addCase(cancellationOrderRequest.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(cancellationOrderRequest.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        const { salesOrderId } = data;
        return {
          ...state,
          orders: state.orders.map((a) =>
            a.salesOrderId === salesOrderId
              ? {
                  ...a,
                  cancellation: {
                    userName: data.userName,
                    userEmail: data.userEmail,
                    requestedDateTime: moment.utc().format(),
                  },
                  fullUpdateHistory: [
                    ...a.fullUpdateHistory,
                    `Cancellation Request generated by ${data.userName} (${data.userEmail}), ${data.requestedDateTime},`,
                  ],
                }
              : a,
          ),
          message: message,
          success: success,
          loading: false,
        };
      })
      .addCase(cancellationOrderRequest.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(markOrderDelivered.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(markOrderDelivered.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        const { salesOrderId } = data;
        const editedOrders = state.vendorOrders.map((a) => {
          if (a.salesOrderId === salesOrderId)
            return {
              ...a,
              orderStatus: "Delivered",
            };
          else return a;
        });
        return {
          ...state,
          vendorOrders: editedOrders,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(markOrderDelivered.rejected, (state, action) => {
        const { message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: false,
        };
      })
      .addCase(markMultipleOrderDelivered.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(markMultipleOrderDelivered.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        const { salesOrderId } = data;
        const editedOrders = state.vendorOrders.map((a) => {
          if (a.salesOrderId === salesOrderId)
            return {
              ...a,
              orderStatus: "Delivered",
            };
          else return a;
        });
        return {
          ...state,
          vendorOrders: editedOrders,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(markMultipleOrderDelivered.rejected, (state, action) => {
        const { message } = action.payload;
        return {
          ...state,
          loading: false,
          // message: message,
          // success: false,
        };
      })
      .addCase(generateDeeplink.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(generateDeeplink.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        const { salesOrderId, deeplink } = data;
        const editedOrders = state.integrationOrders.map((a) => {
          if (a.salesOrderId === salesOrderId)
            return {
              ...a,
              deeplinkUrl: deeplink,
            };
          else return a;
        });
        return {
          ...state,
          integrationOrders: editedOrders,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(generateDeeplink.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(getCateringApiToken.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(getCateringApiToken.fulfilled, (state, action) => {
        const { access_token, expires_in } = action.payload;
        return {
          ...state,
          loading: false,
          cateringApiToken: {
            token: access_token,
            expiresAt: expires_in,
          },
        };
      })
      .addCase(getCateringApiToken.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          success: false,
          message: action.payload,
        };
      })
      .addCase(getFbosByAirport.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(getFbosByAirport.fulfilled, (state, action) => {
        // const { data } = action.payload;
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(getFbosByAirport.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          showMessage: true,
          success: false,
        };
      })
      .addCase(saveVendorOrderDashboardUserView.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(saveVendorOrderDashboardUserView.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        return {
          ...state,
          loading: false,
          vendorViews: [
            {
              id: data.id,
              viewId: data.views[0].id,
              name: data.views[0].name,
              type: data.views[0].type,
              addedOn: data.views[0].addedOn,
              columns: data.views[0].columns,
              columnOrdering: data.views[0].columnOrdering,
            },
          ],
          message: message,
          success: success,
        };
      })
      .addCase(saveVendorOrderDashboardUserView.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(updateOrderHeader.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(updateOrderHeader.fulfilled, (state, action) => {
        const { data, success } = action.payload;
        const { request, fbo, orderId } = data;
        const { flightDetails, orderContact, requestedDeliveryDateTime } =
          request;
        const editedOrders = state.orders.map((a) => {
          if (a.salesOrderId === orderId)
            return {
              ...a,
              fbo: {
                ...a.fbo,
                name: fbo.fboName,
              },
              deliverydatetime: requestedDeliveryDateTime,
              ...(flightDetails &&
              (flightDetails.flightReference || flightDetails.tailNumber)
                ? {
                    flightDetails: {
                      ...a.flightDetails,
                      ...(flightDetails.flightReference && {
                        flightReference: flightDetails.flightReference,
                      }),
                      ...(flightDetails.tailNumber && {
                        tailNumber: flightDetails.tailNumber,
                      }),
                    },
                  }
                : {}),

              ...(orderContact && (orderContact.email || orderContact.phone)
                ? {
                    customer: {
                      ...a.customer,
                      contact: {
                        ...a.customer?.contact,
                        ...(orderContact.email && {
                          email: orderContact.email,
                        }),
                        ...(orderContact.phone && {
                          phone: orderContact.phone,
                        }),
                      },
                    },
                  }
                : {}),
            };
          else return a;
        });
        return {
          ...state,
          orders: editedOrders,
          loading: false,
          message: "Order updated successfully",
          success: success,
        };
      })
      .addCase(updateOrderHeader.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: "Something went wrong. Please try again later.",
          success: false,
        };
      })
      .addCase(updateKitchenProcessingStatus.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(updateKitchenProcessingStatus.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        const { salesOrderId, rawStatus } = data;
        const editedOrders = state.kitchenOrders.map((a) => {
          if (a.salesOrderId === salesOrderId)
            return {
              ...a,
              orderStatus: rawStatus,
            };
          else return a;
        });
        return {
          ...state,
          kitchenOrders: editedOrders,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(updateKitchenProcessingStatus.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(updateMutpleOrdersKitchenProcessingStatus.pending, (state) => {
        return state;
      })
      .addCase(
        updateMutpleOrdersKitchenProcessingStatus.fulfilled,
        (state, action) => {
          const { data } = action.payload;
          const { salesOrderId, rawStatus } = data;
          const editedOrders = state.kitchenOrders.map((a) => {
            if (a.salesOrderId === salesOrderId)
              return {
                ...a,
                orderStatus: rawStatus,
              };
            else return a;
          });
          return {
            ...state,
            kitchenOrders: editedOrders,
            loading: false,
            // message: message,
            // success: success,
          };
        },
      )
      .addCase(
        updateMutpleOrdersKitchenProcessingStatus.rejected,
        (state, action) => {
          return {
            ...state,
            loading: false,
            // message: action.payload,
            // success: false,
          };
        },
      )
      .addCase(fetchIntegratorByAlias.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchIntegratorByAlias.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(fetchIntegratorByAlias.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          showMessage: true,
          success: false,
        };
      })
      .addCase(signOut.fulfilled, (state) => {
        return initialState;
      });
  },
});

export const {
  clearMessage,
  setSearchText,
  setSearchField,
  setSelectedStatus,
  setSelectedAirport,
  setSelectedFbo,
  setSelectedKitchenFilter,
  setSelectedCaterer,
  setSelectedPage,
  setSortField,
  setSortOrder,
  setFilter,
  setDate,
  setVendorDate,
  setKitchenDate,
  setActionRequiredDate,
  setRejectedOrderDate,
  resetFilter,
  setCompactUI,
} = orderSlice.actions;

export default orderSlice.reducer;
